/* eslint-disable react/prop-types */
/**
 * UsPortfolioHealthTab — Phase 4 of the US Portfolio.
 *
 * Mirrors the CSE `PortfolioHealthTab` 1:1 in layout and UX with these
 * intentional adaptations:
 *
 *   * USD currency throughout (no LKR / WHT).
 *   * Drift colour bands per user spec: ≤5% = green, 5–15% = amber, >15% = red.
 *   * Concentration alert banner when a single holding > 20% of portfolio.
 *   * Sector breakdown rendered side-by-side: current vs target with a
 *     drift status pill (green/amber/red).
 *   * Signal mix card — STRONG_BUY / BUY / WATCH / HOLD / AVOID counts.
 *   * "Smart Monthly Investment" dialog launcher — opens
 *     `UsMonthlyInvestmentDialog` with the portfolio's investment_style.
 *   * Pending plans card surfaces saved Option-C plans with Execute /
 *     Discard actions.
 *   * No exit-history (CSE has it; US has no equivalent collection yet).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity, AlertTriangle, CheckCircle2, Pencil, Save, X, Loader2,
  Scale, TrendingUp, Sparkles, Calendar, PlayCircle, Trash2, Info,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
} from "recharts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiGet, apiPatch, apiPost, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import UsMonthlyInvestmentDialog from "./UsMonthlyInvestmentDialog";
import UsAiSuggestionDialog from "./UsAiSuggestionDialog";

const fmtUsd = (v, opts = {}) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (opts.compact && Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (opts.compact && Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (opts.compact && Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtPct = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);

// ---- Drift status colour helpers (per Phase 4 spec) -----------------------

const DRIFT_BADGE = {
  green: {
    cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    label: "On target",
    Icon: CheckCircle2,
  },
  amber: {
    cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    label: "Drifting",
    Icon: AlertTriangle,
  },
  red: {
    cls: "bg-destructive/15 text-destructive border-destructive/30",
    label: "Out of band",
    Icon: AlertTriangle,
  },
};

const DRIFT_TEXT_COLOUR = {
  green: "text-muted-foreground",
  amber: "text-amber-700 dark:text-amber-400",
  red: "text-destructive",
};

const SIGNAL_BADGE = {
  STRONG_BUY: "bg-emerald-600 text-white border-emerald-700",
  BUY: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  WATCH: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  HOLD: "bg-muted text-muted-foreground border-muted-foreground/30",
  AVOID: "bg-destructive/15 text-destructive border-destructive/30",
};

// ---- Health gauge ---------------------------------------------------------

const HEALTH_COLOURS = {
  good: "hsl(var(--success, 142 71% 45%))",
  fair: "hsl(var(--warning, 38 92% 50%))",
  poor: "hsl(var(--destructive))",
};

const HealthGauge = ({ score, label, colour }) => {
  const radius = 58;
  const circumference = Math.PI * radius;
  const ratio = Math.min(1, Math.max(0, (score || 0) / 100));
  const strokeColour = HEALTH_COLOURS[colour] || "hsl(var(--primary))";
  return (
    <div className="flex flex-col items-center" data-testid="us-health-gauge">
      <svg width="160" height="100" viewBox="0 0 160 100">
        <path d="M 20 90 A 60 60 0 0 1 140 90" fill="none" stroke="hsl(var(--muted))" strokeWidth="14" strokeLinecap="round" />
        <path
          d="M 20 90 A 60 60 0 0 1 140 90"
          fill="none"
          stroke={strokeColour}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          style={{ transition: "stroke-dashoffset 600ms ease" }}
        />
        <text x="80" y="78" textAnchor="middle" fontSize="32" fontWeight="600" fill="hsl(var(--foreground))">
          {score}
        </text>
        <text x="80" y="95" textAnchor="middle" fontSize="11" fill="hsl(var(--muted-foreground))">
          / 100
        </text>
      </svg>
      <div className="mt-1 flex items-center gap-1.5">
        <span className="text-sm font-semibold" style={{ color: strokeColour }}>{label}</span>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="About the health score"
              data-testid="us-health-score-info-trigger"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="start"
            className="w-80 text-xs leading-relaxed"
            data-testid="us-health-score-info-popover"
          >
            <div className="font-semibold text-sm mb-1.5">About your health score</div>
            <p className="text-muted-foreground mb-2">
              <strong className="text-foreground">{score}/100</strong> is computed from your
              actual holdings. <strong>100/100 is virtually impossible</strong> in
              practice — every real-world portfolio carries some unavoidable
              imperfection:
            </p>
            <ul className="list-disc list-inside space-y-0.5 mb-2 text-muted-foreground">
              <li>Whole-share rounding creates small sector drifts.</li>
              <li>Composite signals fluctuate daily as fundamentals update.</li>
              <li>Price moves continuously rebalance position weights.</li>
            </ul>
            <div className="mt-2 pt-2 border-t space-y-1">
              <div className="text-foreground font-medium mb-1">Target ranges:</div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">75–85</span>
                <span className="text-emerald-700 dark:text-emerald-400">Good ✅</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">85–92</span>
                <span className="text-emerald-700 dark:text-emerald-400">Excellent ✅✅</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">92+</span>
                <span className="text-amber-700 dark:text-amber-400">Outstanding 🏆</span>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

/**
 * Component-level breakdown of the 0-100 Health score so users can see
 * exactly which lever moves their score. Renders a tiny stacked bar per
 * component with its earned/max points + a one-line explainer.
 */
const HealthScoreBreakdown = ({ components }) => {
  if (!components) return null;
  const ORDER = ["sector_balance", "signal_quality", "concentration", "diversification"];
  const COMPONENT_COLOURS = {
    sector_balance: "bg-primary",
    signal_quality: "bg-emerald-500",
    concentration: "bg-amber-500",
    diversification: "bg-purple-500",
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="us-health-score-breakdown">
      {ORDER.map((k) => {
        const c = components[k];
        if (!c) return null;
        const pct = c.max > 0 ? (c.score / c.max) * 100 : 0;
        return (
          <div
            key={k}
            className="rounded-md border bg-background p-3 space-y-1.5"
            data-testid={`us-health-score-${k}`}
          >
            <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
              <span>{c.label}</span>
              <span className="tabular-nums font-medium text-foreground">
                {c.score}/{c.max}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all ${COMPONENT_COLOURS[k] || "bg-primary"}`}
                style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
              />
            </div>
            <div className="text-[11px] text-muted-foreground leading-snug">
              {c.detail}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const KpiMini = ({ label, value, accent = "muted" }) => {
  const tones = {
    muted: "text-foreground",
    destructive: "text-destructive",
    warning: "text-amber-700 dark:text-amber-400",
  };
  return (
    <div className="rounded-md border bg-background p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${tones[accent]}`}>{value}</div>
    </div>
  );
};

// ---- Concentration alert banner (>20% any single holding) ----------------

const ConcentrationBanner = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div
      className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-1.5"
      data-testid="us-health-concentration-banner"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-destructive">
        <AlertTriangle className="h-4 w-4" />
        Concentration risk detected
      </div>
      {alerts.map((a) => (
        <div
          key={a.us_symbol}
          className="text-xs text-foreground"
          data-testid={`us-health-concentration-${a.us_symbol}`}
        >
          {a.message}
        </div>
      ))}
    </div>
  );
};

// ---- Signal mix card -------------------------------------------------------

const SignalMixCard = ({ mix }) => {
  const ORDER = ["STRONG_BUY", "BUY", "WATCH", "HOLD", "AVOID"];
  const total = ORDER.reduce((acc, k) => acc + (mix?.[k] || 0), 0);
  return (
    <Card data-testid="us-health-signal-mix">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" /> Signal mix
        </CardTitle>
        <CardDescription className="text-xs">
          Live composite-signal counts across all current holdings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-5 gap-2">
          {ORDER.map((k) => {
            const c = mix?.[k] || 0;
            const pct = total > 0 ? Math.round((c / total) * 100) : 0;
            return (
              <div
                key={k}
                className="rounded-md border p-2 text-center bg-background"
                data-testid={`us-health-signal-${k}`}
              >
                <Badge variant="outline" className={`text-[9px] font-medium border ${SIGNAL_BADGE[k]}`}>
                  {k.replace("_", " ")}
                </Badge>
                <div className="mt-1.5 text-lg font-semibold tabular-nums">{c}</div>
                <div className="text-[10px] text-muted-foreground tabular-nums">{pct}%</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ---- Pending plans card ---------------------------------------------------

const PendingPlansCard = ({ portfolioId, plans, onChanged }) => {
  if (!plans || plans.length === 0) return null;
  const handleExecute = async (plan) => {
    if (!window.confirm(
      `Execute "${plan.notes || `Plan ${plan.plan_id.slice(0, 8)}`}" — ` +
      `${plan.selected_picks?.length || 0} positions for ${fmtUsd(plan.amount_usd)}?`
    )) return;
    try {
      const res = await apiPost(
        `/us-portfolios/${portfolioId}/monthly-plans/${plan.plan_id}/execute`,
        {},
      );
      const failed = (res.failed || []).length;
      const ok = (res.executed || []).length;
      if (failed > 0) {
        toast.warning(`Executed ${ok} positions · ${failed} failed`, {
          description: res.failed.map((f) => `${f.us_symbol}: ${f.reason}`).join("\n"),
        });
      } else {
        toast.success(`Executed ${ok} positions`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execute failed");
    }
  };
  const handleDiscard = async (plan) => {
    if (!window.confirm("Discard this pending plan?")) return;
    try {
      await apiDelete(`/us-portfolios/${portfolioId}/monthly-plans/${plan.plan_id}`);
      toast.success("Plan discarded");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discard failed");
    }
  };

  return (
    <Card data-testid="us-health-pending-plans">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" /> Pending monthly plans ({plans.length})
        </CardTitle>
        <CardDescription className="text-xs">
          Saved investment plans you can review and execute when ready.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {plans.map((p) => (
          <div
            key={p.plan_id}
            className="rounded-md border bg-background p-3 flex flex-wrap items-center gap-3"
            data-testid={`us-health-pending-plan-${p.plan_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">
                {p.notes || `Plan ${p.plan_id.slice(0, 8)}`}
              </div>
              <div className="text-xs text-muted-foreground">
                {p.selected_picks?.length || 0} positions · {fmtUsd(p.amount_usd)} ·{" "}
                {new Date(p.created_at).toLocaleDateString()}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-1">
                {(p.selected_picks || []).slice(0, 6).map((pk) => (
                  <Badge key={pk.us_symbol} variant="secondary" className="text-[9px] font-mono">
                    {pk.us_symbol} ×{pk.suggested_shares || pk.shares}
                  </Badge>
                ))}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleExecute(p)}
              data-testid={`us-health-execute-plan-${p.plan_id}`}
            >
              <PlayCircle className="h-3.5 w-3.5 mr-1.5" /> Execute
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDiscard(p)}
              data-testid={`us-health-discard-plan-${p.plan_id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ---- Main panel -----------------------------------------------------------

const UsPortfolioHealthTab = ({ portfolioId, onChanged, onRequestAddHolding }) => {
  const [data, setData] = useState(null);
  const [pendingPlans, setPendingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTarget, setEditingTarget] = useState(false);
  const [draftTargets, setDraftTargets] = useState({});
  const [savingTarget, setSavingTarget] = useState(false);
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const load = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const [h, plans] = await Promise.all([
        apiGet(`/us-portfolios/${portfolioId}/health`),
        apiGet(`/us-portfolios/${portfolioId}/monthly-plans?status=pending`),
      ]);
      setData(h);
      setPendingPlans(plans || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load health");
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => { load(); }, [load]);

  const startEditingTarget = () => {
    const map = {};
    (data?.sector_breakdown || []).forEach((row) => {
      map[row.sector] = row.target_pct != null ? row.target_pct : 0;
    });
    setDraftTargets(map);
    setEditingTarget(true);
  };

  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      // Filter out zero-target sectors so we don't bloat the doc.
      const cleaned = Object.fromEntries(
        Object.entries(draftTargets)
          .map(([k, v]) => [k, Number(v) || 0])
          .filter(([, v]) => v > 0)
      );
      await apiPatch(`/us-portfolios/${portfolioId}`, { sector_targets: cleaned });
      toast.success("Target allocation saved");
      setEditingTarget(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save targets");
    } finally {
      setSavingTarget(false);
    }
  };

  const totalDraftPct = useMemo(
    () => Object.values(draftTargets).reduce((s, v) => s + (Number(v) || 0), 0),
    [draftTargets],
  );

  if (loading && !data) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
          Calculating portfolio health…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">{error}</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const breakdown = data.sector_breakdown || [];
  const hasTarget = data.has_explicit_target;
  const styleLabel = (data.investment_style || "balanced").toUpperCase();
  const isEmpty = (data.holdings_count || 0) === 0;

  return (
    <div className="space-y-5" data-testid="us-portfolio-health-tab">
      {/* Empty-portfolio onboarding CTA (Fix 3) — shown ONLY when the
          portfolio has zero holdings. Guides new users straight to the
          AI Suggestions flow or the manual Add holding dialog. */}
      {isEmpty && (
        <Card
          className="border-2 border-dashed border-primary/40 bg-primary/5"
          data-testid="us-health-empty-cta"
        >
          <CardContent className="py-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/15">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1.5">
              <div className="text-lg font-semibold">
                Your portfolio is empty
              </div>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Get AI-powered stock suggestions tailored for your{" "}
                <span className="font-medium text-foreground">{styleLabel}</span>{" "}
                style — Claude analyses your investment_style, risk profile, and the
                live BUY/STRONG_BUY US universe to recommend 5–10 picks you can
                add with one click.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                onClick={() => setAiOpen(true)}
                data-testid="us-health-empty-ai-btn"
                size="lg"
                className="border-primary"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Get AI Suggestions <span className="opacity-70 ml-1">(~$0.10)</span>
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => onRequestAddHolding?.()}
                data-testid="us-health-empty-manual-btn"
              >
                <Pencil className="h-4 w-4 mr-2" /> Add stocks manually
              </Button>
            </div>
            <div className="text-[11px] text-muted-foreground">
              Style: <span className="font-medium text-foreground">{styleLabel}</span>{" "}
              · Risk: <span className="font-medium text-foreground">{(data.risk_profile || "moderate").toUpperCase()}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Explainer card */}
      <Card className="border-dashed border-primary/30 bg-primary/5" data-testid="us-health-explainer">
        <CardContent className="p-4 text-sm leading-relaxed">
          <div className="flex items-start gap-2">
            <Activity className="h-4 w-4 mt-0.5 text-primary shrink-0" />
            <div className="space-y-1.5">
              <div className="font-medium">What is Portfolio Health?</div>
              <p className="text-muted-foreground">
                A 0–100 score that combines (a) the average composite score
                of every holding, weighted by position size; (b) sector
                concentration risk (any single position over 20% drops the
                score); (c) drift from your target allocation; and (d) any
                holdings flagged AVOID. <strong>Above 70 = healthy.</strong>{" "}
                50–70 = monitor. Below 50 = action recommended.
              </p>
              <p className="text-muted-foreground">
                Use the <strong>Smart Monthly Investment</strong> button below to
                generate a buy plan that gradually rebalances toward your
                {" "}{styleLabel} target — without selling anything.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Health header strip */}
      <Card>
        <CardContent className="py-5 space-y-4">
          <div className="flex flex-col md:flex-row gap-5 items-center md:items-stretch">
            <HealthGauge score={data.health_score} label={data.health_label} colour={data.health_colour} />
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
              <KpiMini label="Holdings" value={data.holdings_count} />
              <KpiMini label="Total value" value={fmtUsd(data.total_value_usd, { compact: true })} />
              <KpiMini
                label="Strong AVOID"
                value={data.exit_signals?.strong || 0}
                accent={(data.exit_signals?.strong || 0) > 0 ? "destructive" : "muted"}
              />
              <KpiMini
                label="Review (WATCH)"
                value={data.exit_signals?.review || 0}
                accent={(data.exit_signals?.review || 0) > 0 ? "warning" : "muted"}
              />
            </div>
          </div>
          {/* Score component breakdown — shows exactly which component
              is dragging the score down, so users aren't left guessing. */}
          {data.health_score_components && (
            <div className="pt-4 border-t">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                Score breakdown (why {data.health_score}/100?)
              </div>
              <HealthScoreBreakdown components={data.health_score_components} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Concentration alerts banner */}
      <ConcentrationBanner alerts={data.concentration_alerts} />

      {/* Pending plans (above the sector card so users see them first) */}
      <PendingPlansCard
        portfolioId={portfolioId}
        plans={pendingPlans}
        onChanged={() => { load(); onChanged?.(); }}
      />

      {/* Sector allocation card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Scale className="h-4 w-4 text-primary" /> Sector allocation
                <Badge variant="outline" className="text-[10px] ml-1">
                  {styleLabel}
                </Badge>
              </CardTitle>
              <CardDescription>
                {hasTarget
                  ? "Current vs target allocation. Drift bands: green ≤5% · amber 5–15% · red >15%."
                  : "No target allocation set. Click Set targets to unlock drift warnings."}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAiOpen(true)}
                data-testid="us-health-ai-suggestion-btn"
                className="border-primary/40 text-primary hover:bg-primary/10"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Get AI Suggestions
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={() => setMonthlyOpen(true)}
                data-testid="us-health-monthly-investment-btn"
              >
                <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Smart Monthly Investment
              </Button>
              {!editingTarget ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={startEditingTarget}
                  data-testid="us-health-edit-targets-btn"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  {hasTarget ? "Edit targets" : "Set targets"}
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEditingTarget(false)}>
                    <X className="h-3.5 w-3.5 mr-1" /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveTarget}
                    disabled={savingTarget}
                    data-testid="us-health-save-targets-btn"
                  >
                    {savingTarget ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                    Save
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Bar chart */}
          {hasTarget && breakdown.length > 0 ? (
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={breakdown} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                  <XAxis dataKey="sector" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" />
                  <YAxis tick={{ fontSize: 10 }} unit="%" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="current_pct" name="Current %" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="target_pct" name="Target %" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : null}

          {/* Drift table / editor */}
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Sector</th>
                  <th className="px-3 py-2 text-right">Current %</th>
                  <th className="px-3 py-2 text-right">Value (USD)</th>
                  {(hasTarget || editingTarget) && <th className="px-3 py-2 text-right">Target %</th>}
                  {hasTarget && <th className="px-3 py-2 text-right">Drift</th>}
                  {hasTarget && <th className="px-3 py-2 text-center">Status</th>}
                </tr>
              </thead>
              <tbody>
                {breakdown.map((row) => {
                  const meta = DRIFT_BADGE[row.drift_status] || DRIFT_BADGE.green;
                  const StatusIcon = meta.Icon;
                  const driftCls = DRIFT_TEXT_COLOUR[row.drift_status] || "text-muted-foreground";
                  return (
                    <tr key={row.sector} className="border-b last:border-0" data-testid={`us-health-sector-row-${row.sector.replace(/\s+/g, "-")}`}>
                      <td className="px-3 py-2.5 font-medium">{row.sector}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{row.current_pct?.toFixed(1)}%</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-xs text-muted-foreground">
                        {fmtUsd(row.current_value_usd, { compact: true })}
                      </td>
                      {(hasTarget || editingTarget) && (
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {editingTarget ? (
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={draftTargets[row.sector] ?? 0}
                              onChange={(e) => setDraftTargets((d) => ({
                                ...d, [row.sector]: e.target.value,
                              }))}
                              className="h-8 w-20 text-right tabular-nums ml-auto"
                              data-testid={`us-health-target-input-${row.sector.replace(/\s+/g, "-")}`}
                            />
                          ) : row.target_pct != null ? (
                            `${row.target_pct.toFixed(1)}%`
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      )}
                      {hasTarget && (
                        <td className={`px-3 py-2.5 text-right tabular-nums ${driftCls}`}>
                          {row.drift_pct != null
                            ? `${row.drift_pct >= 0 ? "+" : ""}${row.drift_pct.toFixed(1)}%`
                            : "—"}
                        </td>
                      )}
                      {hasTarget && (
                        <td className="px-3 py-2.5 text-center">
                          <Badge
                            variant="outline"
                            className={`text-[10px] inline-flex items-center gap-1 ${meta.cls}`}
                            data-testid={`us-health-drift-status-${row.sector.replace(/\s+/g, "-")}`}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {meta.label}
                          </Badge>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {editingTarget && (
                <tfoot>
                  <tr className="border-t bg-muted/20">
                    <td className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">Total</td>
                    <td colSpan={2} className="px-3 py-2 text-right text-muted-foreground">—</td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        Math.abs(totalDraftPct - 100) > 1 ? "text-amber-700 dark:text-amber-400" : "text-foreground"
                      }`}
                    >
                      {totalDraftPct.toFixed(1)}%
                      <span className="ml-2 text-[11px] text-muted-foreground">(target ~100%)</span>
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Signal mix card */}
      <SignalMixCard mix={data.signal_mix} />

      {/* Holdings sorted by signal strength */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            Holdings by signal strength
          </CardTitle>
          <CardDescription>
            Sorted strongest first (STRONG_BUY → BUY → WATCH → HOLD → AVOID).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data.holdings_sorted || []).map((h) => (
            <div
              key={h.holding_id}
              className="rounded-md border bg-background p-2.5 flex flex-wrap items-center gap-3"
              data-testid={`us-health-holding-row-${h.us_symbol}`}
            >
              <div className="min-w-0 flex-1">
                <Link
                  to={`/us-stocks/${encodeURIComponent(h.us_symbol)}`}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {h.company_name}
                </Link>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <span className="font-mono">{h.us_symbol}</span>
                  <span>{h.sector}</span>
                  <span className="tabular-nums">· weight {h.weight_pct?.toFixed(1)}%</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`text-[10px] ${SIGNAL_BADGE[h.composite_signal] || SIGNAL_BADGE.HOLD}`}>
                  {(h.composite_signal || "—").replace("_", " ")}
                  {h.composite_score != null ? ` · ${h.composite_score}` : ""}
                </Badge>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {fmtUsd(h.current_value_usd, { compact: true })}
                </span>
                {h.exit_signal_status === "strong" && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-destructive/15 text-destructive border-destructive/30"
                    title={h.exit_signal_reason}
                  >
                    🔴 Avoid
                  </Badge>
                )}
                {h.exit_signal_status === "review" && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30"
                    title={h.exit_signal_reason}
                  >
                    🟡 Watch
                  </Badge>
                )}
              </div>
            </div>
          ))}
          {(!data.holdings_sorted || data.holdings_sorted.length === 0) && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No holdings yet.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Smart Monthly Investment dialog */}
      <UsMonthlyInvestmentDialog
        open={monthlyOpen}
        onClose={() => setMonthlyOpen(false)}
        portfolioId={portfolioId}
        investmentStyle={data.investment_style}
        onPlanSaved={() => { load(); onChanged?.(); }}
        onExecuted={() => { load(); onChanged?.(); }}
      />

      {/* AI Suggestion dialog (Phase 5) */}
      <UsAiSuggestionDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        portfolioId={portfolioId}
        investmentStyle={data.investment_style}
        onHoldingsChanged={() => { load(); onChanged?.(); }}
      />
    </div>
  );
};

export default UsPortfolioHealthTab;
